import React from "react";
import {
  useCountryId,
  useLoggedInUserStore,
  useMyStore,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import { useUserPreference } from "@coworker/app/src/hooks/useProfilePreferencesQuery";
import userPreferences from "@coworker/enums/profilePreferences";
import {
  PENDO_API_KEY,
  PENDO_APPROVED_COUNTRIES,
} from "../constants/pendoApprovedCountries";
import { useGraphUserProfile } from "./useGraphUserProfile";

function loadPendoScript(): void {
  if ((window as any).pendo) return;

  const o = ((window as any).pendo = (window as any).pendo || {});
  o._q = o._q || [];
  const methods = [
    "initialize",
    "identify",
    "updateOptions",
    "pageLoad",
    "track",
  ];
  for (const m of methods) {
    o[m] =
      o[m] ||
      function (...args: string[]) {
        o._q[m === methods[0] ? "unshift" : "push"]([m].concat(args));
      };
  }

  const script = document.createElement("script");
  script.async = true;
  script.src =
    "https://cdn.pendo.io/agent/static/" + PENDO_API_KEY + "/pendo.js";
  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

async function generateSHA256Hash(text: string): Promise<string> {
  const textAsBuffer = new TextEncoder().encode(text);
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", textAsBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((item) => item.toString(16).padStart(2, "0")).join("");
}

export function usePendo() {
  const usageLocation = useCountryId();
  const email = useLoggedInUserStore((state) => state.email);
  const store = useMyStore();
  const [selectedLanguage] = useUserPreference(
    userPreferences.LANGUAGE,
    store?.primaryLocale ?? ""
  );
  const { data: graphProfile } = useGraphUserProfile();

  const pendoInitialized = React.useRef<boolean>(false);
  const graphProfileRef = React.useRef(graphProfile);

  React.useEffect(() => {
    graphProfileRef.current = graphProfile;
  }, [graphProfile]);

  React.useEffect(() => {
    if (pendoInitialized.current) return;
    if (!email || !usageLocation) return;

    const shouldInitialize = PENDO_APPROVED_COUNTRIES.includes(usageLocation);
    if (!shouldInitialize) return;

    loadPendoScript();

    generateSHA256Hash(email)
      .then((hashedEmail) => {
        if (pendoInitialized.current) return;
        if (!(window as any).pendo?.initialize) return;

        const profile = graphProfileRef.current;
        (window as any).pendo.initialize({
          visitor: {
            id: hashedEmail,
            jobtitle: profile?.jobTitle ?? undefined,
            department: profile?.department ?? undefined,
            primarysite: profile?.officeLocation ?? undefined,
            country: usageLocation,
            businessunit: profile?.companyName ?? undefined,
            role: "application_user",
            selectedlanguage: selectedLanguage,
          },
        });
        pendoInitialized.current = true;
      })
      .catch((e) => {
        console.warn("Failed to initialize Pendo", e);
      });
  }, [email, usageLocation, selectedLanguage]);

  React.useEffect(() => {
    if (!pendoInitialized.current) return;
    if (!graphProfile) return;
    if (!(window as any).pendo?.updateOptions) return;

    try {
      (window as any).pendo.updateOptions({
        visitor: {
          jobtitle: graphProfile.jobTitle ?? undefined,
          department: graphProfile.department ?? undefined,
          primarysite: graphProfile.officeLocation ?? undefined,
          businessunit: graphProfile.companyName ?? undefined,
        },
      });
    } catch (e) {
      console.warn("Failed to update Pendo with graph profile", e);
    }
  }, [graphProfile]);

  React.useEffect(() => {
    if (!pendoInitialized.current || !selectedLanguage) return;
    if (!(window as any).pendo?.getSerializedMetadata) return;

    try {
      const pendoMetadata = (window as any).pendo.getSerializedMetadata();
      if (!pendoMetadata) return;

      (window as any).pendo.updateOptions({
        ...pendoMetadata,
        visitor: {
          ...pendoMetadata.visitor,
          selectedlanguage: selectedLanguage,
        },
      });
    } catch (e) {
      console.warn("Failed to update Pendo options", e);
    }
  }, [selectedLanguage]);
}
